import { Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  Boxes,
  ChevronDown,
  Hammer,
  Eraser,
  LifeBuoy,
  PackageX,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { Menu, MenuItem, MenuLabel } from '@/design-system/Overlay';

/**
 * The product's modules, for the switcher in the top bar.
 *
 * Each module opens its implemented workspace; end-of-life views share one workflow.
 */
export interface ProductModule {
  id: string;
  /** The licensed module this workspace belongs to; absent means every company has it. */
  module?: string;
  label: string;
  description: string;
  icon: LucideIcon;
  to: string;
  /** Path prefixes that belong to this module, for showing which one you are in. */
  paths?: string[];
}

export const MODULES: ProductModule[] = [
  {
    id: 'asset-management',
    label: 'Asset Management',
    description: 'Register, assign and track every asset',
    icon: Boxes,
    to: '/dashboard',
  },
  {
    id: 'service-desk',
    module: 'SERVICE_DESK',
    label: 'Service Desk',
    description: 'Incidents and service requests',
    icon: LifeBuoy,
    to: '/service-desk',
    paths: ['/service-desk'],
  },
  {
    id: 'amc-warranty',
    module: 'CONTRACTS',
    label: 'AMC / Warranty Renewal',
    description: 'Maintenance contracts and renewals',
    icon: ShieldCheck,
    to: '/contracts',
    paths: ['/contracts'],
  },
  {
    id: 'maintenance',
    module: 'MAINTENANCE',
    label: 'Maintenance',
    description: 'Preventive and breakdown work orders',
    icon: Hammer,
    to: '/maintenance',
    paths: ['/maintenance'],
  },
  {
    id: 'decommission',
    module: 'DECOMMISSION',
    label: 'Decommission',
    description: 'Retire assets from service',
    icon: PackageX,
    to: '/decommission',
    paths: ['/decommission'],
  },
  {
    id: 'data-sanitisation',
    module: 'DECOMMISSION',
    label: 'Data Sanitisation',
    description: 'Record sanitisation evidence before disposal',
    icon: Eraser,
    to: '/data-sanitisation',
    paths: ['/data-sanitisation'],
  },
];

export function ModuleSwitcher() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { hasModule } = useAuth();
  // Only the workspaces this company was sold. The asset register is always among them.
  const available = MODULES.filter((module) => !module.module || hasModule(module.module));
  const current =
    available.find(
      (module) =>
        pathname === `/modules/${module.id}` ||
        module.paths?.some((path) => pathname.startsWith(path)),
    ) ?? available[0] ?? MODULES[0]!;
  const CurrentIcon = current.icon;

  return (
    <Menu
      label={`Switch module, current: ${current.label}`}
      align="start"
      panelClassName="min-w-72"
      trigger={
        <>
          <CurrentIcon />
          <span className="hidden max-w-40 truncate md:inline">{current.label}</span>
          <ChevronDown className="opacity-60" />
        </>
      }
    >
      {(close) => (
        <>
          <MenuLabel>Modules</MenuLabel>
          {available.map((module) => {
            const Icon = module.icon;
            return (
              <MenuItem
                key={module.id}
                icon={<Icon />}
                description={module.description}
                onClick={() => {
                  close();
                  navigate(module.to);
                }}
              >
                {module.label}
              </MenuItem>
            );
          })}
        </>
      )}
    </Menu>
  );
}

/** Preserve bookmarked module links now that all switcher modules have workspaces. */
export default function ModuleRedirectPage() {
  const { moduleId } = useParams();
  const module = MODULES.find((entry) => entry.id === moduleId);
  return <Navigate to={module?.to ?? '/dashboard'} replace />;
}
